import React, { Suspense, useState, useEffect, useRef } from "react";
import { Canvas, useThree } from "@react-three/fiber";
import { OrbitControls, useProgress, Environment, Html } from "@react-three/drei";
import { USDZExporter } from "three/examples/jsm/exporters/USDZExporter";
import { ARButton, XR, useXR, useHitTest } from '@react-three/xr';
import { ToyotaTundra } from "./Toyota_tundra";
import "./styles.css";
import { PART_MAPPING } from './store/PartMapping';
import { ProductModal } from './store/ProductModal';
import ContactForm from "./ContactForm";
import RegisterForm from "./RegisterForm";
import ShareModal from "./ShareModal";

function ARManager({ theme, isMobile, children }) {
  const { isPresenting, session } = useXR();
  const { scene } = useThree();
  
  // State to hold the full transformation of the placement
  const [placement, setPlacement] = useState(null);
  const reticleRef = useRef();
  const truckGroupRef = useRef();

  // 1. Clear backgrounds
  useEffect(() => {
    if (isPresenting) {
      scene.background = null; 
      document.documentElement.style.setProperty('background', 'transparent', 'important');
      document.body.style.setProperty('background', 'transparent', 'important');
      
      const rootDiv = document.getElementById('root');
      const appDiv = document.querySelector('.App');
      const canvasDiv = document.querySelector('.product-canvas');
      
      if (rootDiv) rootDiv.style.setProperty('background', 'transparent', 'important');
      if (appDiv) appDiv.style.setProperty('background', 'transparent', 'important');
      if (canvasDiv) canvasDiv.style.setProperty('background', 'transparent', 'important');
    } else {
      document.documentElement.style.removeProperty('background');
      document.body.style.removeProperty('background');
      
      const rootDiv = document.getElementById('root');
      const appDiv = document.querySelector('.App');
      const canvasDiv = document.querySelector('.product-canvas');
      
      if (rootDiv) rootDiv.style.removeProperty('background');
      if (appDiv) appDiv.style.removeProperty('background');
      if (canvasDiv) canvasDiv.style.removeProperty('background');
      
      // Reset the truck placement when exiting AR
      setPlacement(null); 
    }
  }, [isPresenting, scene]);

  // 2. Listen for the user tapping the screen to place the truck
  useEffect(() => {
    if (!session) return;
    const handleSelect = () => {
      if (reticleRef.current && reticleRef.current.visible && !placement) {
        // Anchor the truck to the reticle's current real-world position and rotation
        setPlacement({
          position: reticleRef.current.position.clone(),
          quaternion: reticleRef.current.quaternion.clone()
        });
      }
    };
    session.addEventListener('select', handleSelect);
    return () => session.removeEventListener('select', handleSelect);
  }, [session, placement]);

  // 3. Track the physical floor and move the reticle ring
  useHitTest((hitMatrix) => {
    if (reticleRef.current && !placement) {
      reticleRef.current.visible = true;
      hitMatrix.decompose(
        reticleRef.current.position,
        reticleRef.current.quaternion,
        reticleRef.current.scale
      );
    }
  });

  return (
    <>
      {!isPresenting && (
        <color attach="background" args={[theme === 'dark' ? '#111111' : '#f5f5f7']} />
      )}
      
      {/* The Targeting Ring: Only visible in AR before placing the truck */}
      {isPresenting && !placement && (
        <>
          <mesh ref={reticleRef} rotation-x={-Math.PI / 2} visible={false}>
            <ringGeometry args={[0.15, 0.2, 32]} />
            <meshBasicMaterial color="white" transparent opacity={0.8} />
          </mesh>

          {/* Instruction Note for the user */}
          <Html center>
            <div style={{
              background: 'rgba(0,0,0,0.6)',
              color: 'white',
              padding: '12px 24px',
              borderRadius: '30px',
              fontSize: '16px',
              fontWeight: '500',
              backdropFilter: 'blur(8px)',
              pointerEvents: 'none',
              textAlign: 'center',
              width: 'max-content',
              border: '1px solid rgba(255,255,255,0.2)',
              boxShadow: '0 4px 15px rgba(0,0,0,0.3)',
              userSelect: 'none'
            }}>
              ✨ Tap to place your custom rig
            </div>
          </Html>
        </>
      )}

      {/* The Truck */}
      <group 
        ref={truckGroupRef}
        position={
          isPresenting 
            ? (placement ? [placement.position.x, placement.position.y, placement.position.z] : [0, -1000, 0]) 
            : (isMobile ? [0, -0.1, 0] : [1.5, -1.2, 0])
        }
        quaternion={isPresenting && placement ? placement.quaternion : undefined}
        scale={1} 
      >
        {/* Offset the model locally so the front is at the anchor point [0,0,0] instead of the center */}
        <group position={isPresenting ? [0, 0, -5.5] : [0, 0, 0]}>
          {children}
        </group>
      </group>
    </>
  );
}

// --- DEBUG ERROR OVERLAY ---
function DebugErrorOverlay({ error, onClear }) {
  if (!error) return null;
  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
      background: 'rgba(255,0,0,0.95)', color: 'white', zIndex: 99999,
      padding: '40px 20px', overflowY: 'auto', fontSize: '14px', fontWeight: 'bold'
    }}>
      <h2 style={{ color: 'yellow' }}>⚠️ APP ERROR DETECTED</h2>
      <button onClick={onClear} style={{ background: 'white', border: 'none', padding: '10px 20px', borderRadius: '5px', marginBottom: '20px' }}>
        CLEAR & CLOSE
      </button>
      <pre style={{ whiteSpace: 'pre-wrap', background: 'rgba(0,0,0,0.3)', padding: '15px' }}>
        {error}
      </pre>
    </div>
  );
}

function Loader() {
  const { progress, active } = useProgress();
  const [show, setShow] = useState(true);

  useEffect(() => {
    if (!active && progress === 100) {
      const timer = setTimeout(() => setShow(false), 500);
      return () => clearTimeout(timer);
    } else {
      setShow(true);
    }
  }, [active, progress]);

  if (!show) return null;

  return (
    <div className="loading-screen-overlay">
      <div className="loading-screen">
        <h2>FITZA</h2>
        <p style={{ color: '#888', marginTop: '10px' }}>Loading Fully Loaded Garage... {Math.round(progress)}%</p>
        <div className="loader-bar-bg">
          <div className="loader-bar-fill" style={{ width: `${progress}%` }}></div>
        </div>
      </div>
    </div>
  );
}

const PAINT_COLORS = [
  { name: 'Olive Drab', hex: '#2d5a27' },
  { name: 'Desert Tan', hex: '#d2b48c' },
  { name: 'Stealth Black', hex: '#1a1a1a' },
  { name: 'Nardo Gray', hex: '#686a6c' },
  { name: 'TRD Pro Red', hex: '#aa1111' },
];

const WHEEL_COLORS = [
  { name: 'Matte Black', hex: '#111111' },
  { name: 'Gunmetal', hex: '#3b3b3b' },
  { name: 'Bronze', hex: '#5c4d3c' },
];

export default function App() {
  const [debugError, setDebugError] = useState(null);

  useEffect(() => {
    // Catch Global JS Errors
    const handleError = (e) => {
      const msg = e.error ? `${e.error.message}\n${e.error.stack}` : e.message;
      setDebugError(`RUNTIME ERROR:\n${msg}`);
    };
    
    // Catch Failed Promises (like failing Exports or Fetches)
    const handleRejection = (e) => {
      setDebugError(`PROMISE REJECTION:\n${e.reason}`);
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleRejection);

    // Prevent Right-Clicking
    const handleContextMenu = (e) => e.preventDefault();
    document.addEventListener('contextmenu', handleContextMenu);

    // Prevent basic F12 / Ctrl+Shift+I DevTools shortcuts
    const handleKeyDown = (e) => {
      if (
        e.keyCode === 123 || // F12
        (e.ctrlKey && e.shiftKey && e.keyCode === 73) || // Ctrl+Shift+I
        (e.ctrlKey && e.shiftKey && e.keyCode === 74) || // Ctrl+Shift+J
        (e.ctrlKey && e.keyCode === 85) // Ctrl+U (View Source)
      ) {
        e.preventDefault();
      }
    };
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);
  
  const [colors, setColors] = useState({ main: "#2d5a27", wheels: "#111111" });
  const [theme, setTheme] = useState("dark");
  const [panelOpen, setPanelOpen] = useState(false); // For mobile bottom sheet
  
  // Accessory States
  const [bumper, setBumper] = useState("offroad");
  const [showCanopy, setShowCanopy] = useState(true);
  const [showRack, setShowRack] = useState(true);
  const [showFlares, setShowFlares] = useState(true);
  const [showGrill, setShowGrill] = useState(true);
  const [showWinch, setShowWinch] = useState(true);
  const [selectedPart, setSelectedPart] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showCatalog, setShowCatalog] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [catalogData, setCatalogData] = useState({}); // Stores fetched Shopify data

  // Build History Logic: Keeps track of activation order
  const [buildOrder, setBuildOrder] = useState([]);
  
  useEffect(() => {
    const appState = { bumper, showCanopy, showRack, showFlares, showGrill, showWinch };
    const currentActiveIds = PART_MAPPING.filter(p => p.visibility(appState)).map(p => p.id);
    
    setBuildOrder(prev => {
      // 1. Remove parts that are no longer active
      const filtered = prev.filter(id => currentActiveIds.includes(id));
      
      // 2. Find parts that are active but not in our order list yet
      const newlyAdded = currentActiveIds.filter(id => !prev.includes(id));
      
      // 3. Put newly added at the top
      return [...newlyAdded, ...filtered];
    });
  }, [bumper, showCanopy, showRack, showFlares, showGrill, showWinch]);

  // Derived list of parts for the catalog
  const activeParts = buildOrder.map(id => PART_MAPPING.find(p => p.id === id)).filter(Boolean);

  // Background Fetch for Catalog Data
  useEffect(() => {
    const domain = process.env.REACT_APP_SHOPIFY_DOMAIN || 'autoshopping24.com';
    
    activeParts.forEach(async (part) => {
      if (part.shopifyHandle && !catalogData[part.id]) {
        try {
          const response = await fetch(`https://${domain}/products/${part.shopifyHandle}.js`);
          if (response.ok) {
            const data = await response.json();
            setCatalogData(prev => ({
              ...prev,
              [part.id]: {
                image: data.featured_image,
                price: (data.price / 100).toLocaleString("en-US", {style:"currency", currency:"USD"}),
                vendor: data.vendor,
                title: data.title,
                failed: false
              }
            }));
          } else {
            // Mark as failed to trigger fallback link
            setCatalogData(prev => ({
              ...prev,
              [part.id]: { failed: true }
            }));
          }
        } catch (e) {
          setCatalogData(prev => ({
            ...prev,
            [part.id]: { failed: true }
          }));
        }
      }
    });
  }, [activeParts, catalogData]); // Re-run when active parts change or catalog data is updated (careful with infinite loops, but we check if catalogData[part.id] exists)

  // Responsive state for 3D positioning
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const truckRef = useRef();

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    // Show hint after loader clears
    const timer = setTimeout(() => setShowHint(true), 3000);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timer);
    };
  }, []);

  const toggleTheme = () => {
    setTheme(prev => prev === "dark" ? "light" : "dark");
  };

  const captureCanvas = async () => {
    const canvas = document.querySelector('canvas');
    if (!canvas) return null;

    // Create a temporary canvas for compositing (watermark)
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = canvas.width;
    tempCanvas.height = canvas.height;
    const ctx = tempCanvas.getContext('2d');

    // Draw original canvas
    ctx.drawImage(canvas, 0, 0);

    // Add Branded Watermark
    ctx.font = 'bold 32px sans-serif';
    ctx.fillStyle = theme === 'dark' ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.3)';
    ctx.textAlign = 'right';
    ctx.fillText('FITZA.IO', tempCanvas.width - 40, tempCanvas.height - 40);

    return tempCanvas.toDataURL('image/png');
  };

  const handleShare = async (platform) => {
    const screenshot = await captureCanvas();
    if (!screenshot) return;

    const shareData = {
      title: 'My Custom Rig | FITZA',
      text: 'Just built my dream rig on FITZA. Thoughts? 🛻💨',
      url: window.location.href
    };

    // If Instagram, we trigger download as per user request
    if (platform === 'instagram') {
        const link = document.createElement('a');
        link.download = `fitza-build-${Date.now()}.png`;
        link.href = screenshot;
        link.click();
        alert('Image saved to gallery! Post it to your story and tag us.');
        return;
    }

    // Try native share if available (Mobile Messages/WhatsApp etc)
    if (navigator.share && (platform === 'messages' || platform === 'whatsapp')) {
      try {
        // Converting base64 to file for native sharing
        const res = await fetch(screenshot);
        const blob = await res.blob();
        const file = new File([blob], 'my-fitza-build.png', { type: 'image/png' });
        
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
            await navigator.share({
                ...shareData,
                files: [file]
            });
        } else {
            await navigator.share(shareData);
        }
      } catch (e) {
        console.error("Share failed", e);
      }
    } else {
      // Fallback to social URLs
      let shareUrl = '';
      const text = encodeURIComponent(shareData.text);
      const url = encodeURIComponent(shareData.url);

      switch(platform) {
        case 'x':
            shareUrl = `https://twitter.com/intent/tweet?text=${text}&url=${url}`;
            break;
        case 'facebook':
            shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${url}`;
            break;
        case 'whatsapp':
            shareUrl = `https://wa.me/?text=${text}%20${url}`;
            break;
        default:
            navigator.clipboard.writeText(`${shareData.text} ${shareData.url}`);
            alert('Link copied to clipboard!');
            return;
      }
      window.open(shareUrl, '_blank');
    }
  };

  const handleAR = async () => {
    if (!truckRef.current) {
      alert("Model not ready for AR yet. Please wait.");
      return;
    }

    setIsExporting(true);
    // Small delay to ensure any pending material/visibility updates are committed to the GPU/Scene Graph
    await new Promise(resolve => setTimeout(resolve, 500));

    try {
      const exporter = new USDZExporter();
      // USDZExporter.parse is async in modern versions
      const arrayBuffer = await exporter.parse(truckRef.current);
      const blob = new Blob([arrayBuffer], { type: 'model/vnd.usdz+zip' });
      const url = URL.createObjectURL(blob);
      
      const anchor = document.createElement('a');
      anchor.setAttribute('rel', 'ar');
      anchor.setAttribute('href', url);
      const img = document.createElement('img');
      anchor.appendChild(img);
      anchor.click();
      
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (err) {
      console.error("USDZ Export failed:", err);
      setDebugError(`USDZ EXPORT CRASHED:\n${err.message}\n${err.stack}`);
    } finally {
      setIsExporting(false);
    }
  };

  const handlePartClick = async (meshName) => {
    if (process.env.NODE_ENV === 'development') {
      console.log("App received click for:", meshName);
    }
    const lowerMesh = meshName.toLowerCase();
    const appState = { bumper, showCanopy, showRack, showFlares, showGrill, showWinch };
    
    // ROBUST SELECTION MECHANISM:
    // 1. Filter for all mappings that are currently visible and match the mesh name
    const activeMappings = PART_MAPPING.filter(mapping => 
      mapping.visibility(appState) &&
      mapping.fragments.some(fragment => lowerMesh.includes(fragment))
    );

    // 2. Select the "Best Match" by picking the mapping with the longest matching fragment
    const partEntry = activeMappings.sort((a, b) => {
      const aMax = Math.max(...a.fragments.filter(f => lowerMesh.includes(f)).map(f => f.length));
      const bMax = Math.max(...b.fragments.filter(f => lowerMesh.includes(f)).map(f => f.length));
      return bMax - aMax;
    })[0];
    
    if (partEntry && partEntry.shopifyHandle) {
      setSelectedPart({ loading: true, name: partEntry.fallbackName });
      setShowModal(true);
      
      const handle = partEntry.shopifyHandle;
      const domain = process.env.REACT_APP_SHOPIFY_DOMAIN || 'autoshopping24.com';
      
      try {
        const response = await fetch(`https://${domain}/products/${handle}.js`);
        
        if (!response.ok) {
          console.warn(`Product handle '${handle}' not found on Shopify. Using fallback.`);
          setSelectedPart({
            loading: false,
            brand: "Premium Accessory",
            name: partEntry.fallbackName,
            price: "Check Website",
            description: "Detailed information for this specific component is currently being updated. Please visit our store for full specs and pricing.",
            link: `https://${domain}`,
            specs: []
          });
          return;
        }

        const shopifyData = await response.json();
        
        setSelectedPart({
          loading: false,
          brand: shopifyData.vendor,
          name: shopifyData.title,
          price: (shopifyData.price / 100).toLocaleString("en-US", {style:"currency", currency:"USD"}),
          image: shopifyData.featured_image,
          description: shopifyData.description.replace(/<[^>]*>?/gm, '').substring(0, 180) + "...", 
          link: `https://${domain}/products/${handle}`,
          variantId: shopifyData.variants[0].id,
          specs: shopifyData.options && shopifyData.options[0] ? shopifyData.options[0].values : []
        });
      } catch (e) {
        console.error("Fetch error:", e);
        setSelectedPart(prev => ({ ...prev, loading: false }));
      }
    }
  };

  return (
    <div className={`App ${theme === 'light' ? 'light-theme' : ''}`}>
      <DebugErrorOverlay error={debugError} onClear={() => setDebugError(null)} />
      <Loader />
      
      {isExporting && (
        <div className="loading-screen-overlay" style={{ zIndex: 10000 }}>
          <div className="loading-screen">
            <h2>Preparing AR</h2>
            <p style={{ color: '#888', marginTop: '10px' }}>Generating your custom 3D rig...</p>
            <div className="loader-bar-bg">
              <div className="loader-bar-fill exporting"></div>
            </div>
          </div>
        </div>
      )}
      
      {/* Action Buttons for Mobile & Desktop Bottom Bar */}
      {!panelOpen && (
        <div className="mobile-actions-container">
          <button className="mobile-config-toggle" onClick={() => setPanelOpen(true)}>
            ⚙️ Configure
          </button>
          
          <button className="mobile-share-btn build-list-btn" onClick={() => setShowCatalog(true)} title="View Build List">
            <span className="btn-icon">🛒</span>
            {activeParts.length > 0 && <span className="cart-badge">{activeParts.length}</span>}
          </button>

          <button className="mobile-share-btn" onClick={() => setShowShare(true)} title="Share Build">
            <span className="btn-icon">📤</span>
          </button>
          
          {/* iOS Button (USDZ) */}
            {/iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream ? (
              <button className="mobile-share-btn" onClick={handleAR} title="View in AR">
                <span className="btn-icon">✨</span>
              </button>
            ) : (
              /* Android Button (WebXR) with Hit-Testing AND Custom Styling */
              <ARButton 
                className="mobile-share-btn xr-button-custom"
                sessionInit={{ 
                  requiredFeatures: ['hit-test'],
                  optionalFeatures: ['local-floor', 'bounded-floor']
                }}
              >
                <span className="btn-icon">✨</span>
              </ARButton>
            )}
        </div>
      )}

      <div className="product-canvas" style={{ backgroundColor: theme === 'dark' ? '#111111' : '#f5f5f7' }}>
        <Canvas 
          shadows 
          camera={{ position: [7, 3, 7], fov: 35 }}
          gl={{ 
            alpha: true, // MUST BE TRUE FOR AR CAMERA FEED
            antialias: true,
            toneMappingExposure: 0.7
          }}
        >
          <XR>
            <ARManager theme={theme} isMobile={isMobile}>
              <Suspense fallback={null}>
                
                <Environment 
                  files="/studio.hdr" 
                  background={false} 
                  intensity={theme === 'dark' ? 0.7 : 1.2} 
                />
                
                <ambientLight intensity={theme === 'dark' ? 0.2 : 0.4} />
                
                <spotLight 
                  position={[10, 15, 10]} 
                  angle={0.3} 
                  penumbra={1} 
                  intensity={2} 
                  castShadow 
                />
                        
                <group ref={truckRef}>
                  <ToyotaTundra 
                    customColors={colors} 
                    currentBumper={bumper} 
                    showCanopy={showCanopy}
                    showRack={showRack}
                    showFlares={showFlares}
                    showGrill={showGrill}
                    showWinch={showWinch}
                    onPartClick={handlePartClick}
                    isMobile={isMobile}
                  />
                </group>

                <OrbitControls 
                  makeDefault 
                  minPolarAngle={0} 
                  maxPolarAngle={Math.PI} 
                  enablePan={false} 
                  minDistance={5} 
                  maxDistance={15} 
                />
              </Suspense>
            </ARManager>
          </XR>
        </Canvas>
      </div>

      {/* RENDER MODAL IF A PART IS SELECTED */}
      {showModal && selectedPart && (
        <ProductModal 
          data={selectedPart}
          onClose={() => setShowModal(false)} 
        />
      )}

      {/* CATALOG MODAL */}
      {showCatalog && (
        <div className="modal-overlay" onClick={() => setShowCatalog(false)}>
          <div className="product-modal catalog-modal" onClick={(e) => e.stopPropagation()}>
            <button className="close-btn" onClick={() => setShowCatalog(false)}>&times;</button>
            <div className="catalog-header">
              <h2>Current Build</h2>
              <p>{activeParts.length} Parts Configured</p>
            </div>
            <div className="catalog-list">
              {activeParts.length === 0 ? (
                <div className="empty-catalog">
                  <p>Your build is currently stock.</p>
                </div>
              ) : (
                activeParts.map(part => {
                  const liveData = catalogData[part.id];
                  const domain = process.env.REACT_APP_SHOPIFY_DOMAIN || 'autoshopping24.com';
                  
                  // Use fallback link if shopifyHandle is missing OR if the fetch failed
                  const useFallback = !part.shopifyHandle || liveData?.failed;
                  const shopLink = useFallback 
                    ? `https://autoshopping24.com` 
                    : `https://${domain}/products/${part.shopifyHandle}`;

                  return (
                    <div key={part.id} className="catalog-item">
                      <div className="catalog-item-image">
                         <img 
                           src={liveData?.image || '/image_coming_soon.png'} 
                           alt={liveData?.title || part.fallbackName} 
                           onError={(e) => { e.target.src = '/image_coming_soon.png' }}
                         />
                      </div>
                      <div className="catalog-item-info">
                        <h3>{liveData?.title || part.fallbackName}</h3>
                        <p className="catalog-brand">{liveData?.vendor || "Premium Accessory"}</p>
                        {liveData?.price && <p className="catalog-price">{liveData.price}</p>}
                      </div>
                      <a 
                        href={shopLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="catalog-view-btn"
                      >
                        {useFallback ? 'Visit Store' : 'Shop Now'}
                      </a>
                    </div>
                  );
                })
              )}
            </div>
            <div className="catalog-footer">
              <button className="buy-now-main" style={{ marginBottom: 0 }} onClick={() => setShowContact(true)}>
                Inquire About Full Build
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CONTACT FORM MODAL */}
      {showContact && (
        <div className="modal-overlay" onClick={() => setShowContact(false)}>
          <div className="product-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <button className="close-btn" onClick={() => setShowContact(false)}>&times;</button>
            <ContactForm onClose={() => setShowContact(false)} />
          </div>
        </div>
      )}

      {/* REGISTRATION MODAL */}
      {showRegister && (
        <div className="modal-overlay" onClick={() => setShowRegister(false)}>
          <div className="product-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <button className="close-btn" onClick={() => setShowRegister(false)}>&times;</button>
            <RegisterForm onClose={() => setShowRegister(false)} />
          </div>
        </div>
      )}

      {/* SHARE MODAL */}
      {showShare && (
        <ShareModal 
            onClose={() => setShowShare(false)} 
            onShare={handleShare}
        />
      )}

      <div className="ui-layer">
        <div className="header">
          <div className="header-content">
            <h1>FITZA</h1>
            <p className="header-subtitle">Build your custom rig</p>
          </div>
          <div className="header-actions">
            {!isMobile && (
              <button className="theme-toggle catalog-header-btn" onClick={() => setShowCatalog(true)}>
                <span className="btn-icon">🛒</span>
                <span className="btn-text">Build List</span>
                {activeParts.length > 0 && <span className="header-badge">{activeParts.length}</span>}
              </button>
            )}
            <button className="theme-toggle register-btn" onClick={() => setShowRegister(true)}>
              <span className="btn-icon">🚀</span>
              <span className="btn-text">Join Beta</span>
            </button>
            <button className="theme-toggle contact-btn" onClick={() => setShowContact(true)}>
              <span className="btn-icon">✉️</span>
              <span className="btn-text">Contact</span>
            </button>
            <button className="theme-toggle mode-btn" onClick={toggleTheme}>
              <span className="btn-icon">{theme === 'dark' ? '🌙' : '☀️'}</span>
              <span className="btn-text">{theme === 'dark' ? 'Dark Mode' : 'Light Mode'}</span>
            </button>
          </div>
        </div>


        <div className={`configurator-panel ${panelOpen ? 'open' : ''}`}>
          <button className="panel-close-mobile" onClick={() => setPanelOpen(false)} />
          {isMobile && (
            <button className="panel-close-btn-top" onClick={() => setPanelOpen(false)}>✕</button>
          )}

          <div className="config-section">
            <h3>Body Paint</h3>
            <div className="color-picker">
              {PAINT_COLORS.map(c => (
                <div key={c.name} className={`color-swatch ${colors.main === c.hex ? 'active' : ''}`} style={{ backgroundColor: c.hex }} onClick={() => setColors({ ...colors, main: c.hex })} title={c.name} />
              ))}
            </div>
          </div>

          <div className="config-section">
            <h3>Wheel Finish</h3>
            <div className="color-picker">
              {WHEEL_COLORS.map(c => (
                <div key={c.name} className={`color-swatch ${colors.wheels === c.hex ? 'active' : ''}`} style={{ backgroundColor: c.hex }} onClick={() => setColors({ ...colors, wheels: c.hex })} title={c.name} />
              ))}
            </div>
          </div>

          <div className="config-section">
            <h3>Bumpers</h3> 
            <div className="part-selector">
              <button className={`part-btn ${bumper === 'stock' ? 'active' : ''}`} onClick={() => setBumper('stock')}>Stock</button>
              <button className={`part-btn ${bumper === 'offroad' ? 'active' : ''}`} onClick={() => setBumper('offroad')}>Off-Road</button>
            </div>
          </div>

          <div className="config-section">
            <h3>Accessories</h3>
            <div className="part-selector">
              <button className={`part-btn ${showCanopy ? 'active' : ''}`} onClick={() => setShowCanopy(!showCanopy)}>Canopy</button>
              <button className={`part-btn ${showRack ? 'active' : ''}`} onClick={() => setShowRack(!showRack)}>Roof Rack</button>
              <button className={`part-btn ${showFlares ? 'active' : ''}`} onClick={() => setShowFlares(!showFlares)}>Flares</button>
              <button className={`part-btn ${showGrill ? 'active' : ''}`} onClick={() => setShowGrill(!showGrill)}>Grill</button>
              <button className={`part-btn ${showWinch ? 'active' : ''}`} onClick={() => setShowWinch(!showWinch)}>Winch</button>
            </div>
          </div>

          <div className="config-section coming-soon-teaser" style={{ marginTop: '20px', padding: '20px', background: 'rgba(74, 222, 128, 0.05)', borderRadius: '16px', border: '1px dashed rgba(74, 222, 128, 0.3)' }}>
            <h3 style={{ border: 'none', color: '#4ade80', marginBottom: '8px' }}>Coming Soon</h3>
            <p style={{ fontSize: '0.8rem', color: '#888', margin: '0 0 15px 0', lineHeight: '1.4' }}>
              We're adding new trucks, more parts, and a community garage.
            </p>
            <button 
              className="view-store-link" 
              onClick={() => setShowRegister(true)}
              style={{ padding: '10px', fontSize: '0.75rem', width: '100%', border: 'none' }}
            >
              Get Early Access
            </button>
          </div>
        </div>

        <div className="app-footer">
          <p>© 2026 FITZA. All rights reserved.</p>
        </div>

        {showHint && !showModal && !panelOpen && (
          <div className="interaction-toast" onClick={() => setShowHint(false)}>
            <div className="toast-content">
              <span className="toast-icon">✨</span>
              <div className="toast-text-wrapper">
                <p className="toast-title">Welcome to the FITZA Garage</p>
                <p className="toast-desc">
                  Configure your rig, view it <strong>1:1 Real-Size</strong> in AR, and shop the build.
                </p>
              </div>
              <button className="toast-close">✕</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}